/**
 * SmartBlocks API Client
 *
 * Frontend client for the SmartBlock tracker system.
 */

import { coreApi } from './coreApi';

// ============ Types ============

export interface TrackerInput {
  key: string;
  label: string;
  unit: string;
  baseline: number;
  min: number;
  max: number;
  step: number;
  input_type: 'number' | 'slider';
}

export interface TrackerCategory {
  key: string;
  label: string;
  icon: string;
  inputs: TrackerInput[];
}

export interface TrackerType {
  id: string;
  label: string;
  description: string | null;
  icon_url: string | null;
  categories: TrackerCategory[];
  default_baselines: Record<string, Record<string, number>>;
  recalibration_days: number;
  available_roles: string[];
  tc_cost_per_block: number;
  active: boolean;
}

export interface Enrollment {
  id: string;
  user_id: string;
  tracker_type_id: string;
  baselines: Record<string, Record<string, number>>;
  last_recalibrated_at: string | null;
  cluster_count: number;
  active: boolean;
  created_at: string;
  tracker_type?: TrackerType;
}

export interface SmartBlock {
  id: string;
  enrollment_id: string;
  user_id: string;
  tracker_type_id: string;
  bitblocks: Record<string, { inputs: Record<string, number>; score: number }>;
  composite_score: number;
  gec0_score: number;
  momentum: number;
  csk_factors: CSKFactors;
  trust_score: number;
  sealed: boolean;
  signature: string | null;
  cluster_id: string | null;
  recorded_date: string;
  created_at: string;
}

export interface CSKFactors {
  S: number;
  H: number;
  D: number;
  R: number;
  E: number;
}

export interface SmartBlockCluster {
  id: string;
  enrollment_id: string;
  user_id: string;
  tracker_type_id: string;
  block_count: number;
  momentum_score: number;
  avg_gec0: number;
  avg_composite: number;
  csk_summary: CSKFactors;
  period_start: string;
  period_end: string;
  triggered_recalibration: boolean;
  created_at: string;
}

export interface TrendPoint {
  date: string;
  gec0: number;
  composite: number;
  momentum: number;
}

export interface DashboardData {
  enrollment: Enrollment;
  latest_block: SmartBlock | null;
  streak_days: number;
  trend: TrendPoint[];
  csk_current: CSKFactors | null;
  recent_clusters: SmartBlockCluster[];
}

export interface CoachAthleteData {
  user_id: string;
  name: string;
  avatar_url: string | null;
  enrollment_id: string;
  cluster_count: number;
  latest_block: {
    date: string;
    gec0: number;
    composite: number;
    momentum: number;
    csk_factors: CSKFactors;
  } | null;
}

export interface BlockSubmitDiagnostics {
  category_scores: Record<string, number>;
  csk_factors: CSKFactors;
  momentum: number;
  gec0: number;
}

// ============ API Functions ============

export async function getTrackerTypes(): Promise<TrackerType[]> {
  const res = await coreApi<{ data: TrackerType[] }>('/api/v1/smartblocks/tracker-types');
  return res.data;
}

export async function getEnrollments(): Promise<Enrollment[]> {
  const res = await coreApi<{ data: Enrollment[] }>('/api/v1/smartblocks/enrollments');
  return res.data;
}

export async function enrollTracker(trackerTypeId: string): Promise<Enrollment> {
  const res = await coreApi<{ data: Enrollment }>('/api/v1/smartblocks/enroll', {
    method: 'POST',
    body: { tracker_type_id: trackerTypeId },
  });
  return res.data;
}

export async function submitBlock(params: {
  tracker_type_id: string;
  bitblocks: Record<string, Record<string, number>>;
  recorded_date?: string;
}): Promise<{ data: SmartBlock; diagnostics: BlockSubmitDiagnostics }> {
  return coreApi('/api/v1/smartblocks/blocks', {
    method: 'POST',
    body: params,
  });
}

export async function getBlocks(params: {
  tracker_type_id?: string;
  limit?: number;
  offset?: number;
}): Promise<{ data: SmartBlock[]; total: number }> {
  const query = new URLSearchParams();
  if (params.tracker_type_id) query.set('tracker_type_id', params.tracker_type_id);
  if (params.limit) query.set('limit', String(params.limit));
  if (params.offset) query.set('offset', String(params.offset));
  const qs = query.toString();
  return coreApi(`/api/v1/smartblocks/blocks${qs ? `?${qs}` : ''}`);
}

export async function getDashboard(trackerTypeId: string): Promise<DashboardData> {
  const res = await coreApi<{ data: DashboardData }>(
    `/api/v1/smartblocks/dashboard?tracker_type_id=${trackerTypeId}`,
  );
  return res.data;
}

export async function finalizeCluster(trackerTypeId: string): Promise<SmartBlockCluster> {
  const res = await coreApi<{ data: SmartBlockCluster }>('/api/v1/smartblocks/clusters/finalize', {
    method: 'POST',
    body: { tracker_type_id: trackerTypeId },
  });
  return res.data;
}

export async function getClusters(trackerTypeId?: string): Promise<SmartBlockCluster[]> {
  const qs = trackerTypeId ? `?tracker_type_id=${trackerTypeId}` : '';
  const res = await coreApi<{ data: SmartBlockCluster[] }>(`/api/v1/smartblocks/clusters${qs}`);
  return res.data;
}

export async function getCoachAthletes(trackerTypeId: string): Promise<CoachAthleteData[]> {
  const res = await coreApi<{ data: CoachAthleteData[] }>(
    `/api/v1/smartblocks/coach/athletes?tracker_type_id=${trackerTypeId}`,
  );
  return res.data;
}
